import {
  SidebarInset,
  SidebarProvider,
} from "@projectobelisk/uikit/components/navigation/sidebar";
import { TooltipProvider } from "@projectobelisk/uikit/components/overlays/tooltip";
import { ThemeProvider } from "@projectobelisk/uikit/theme-provider";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { useEffect } from "react";
import { requireAuth } from "@/auth/require-auth";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { AppTopbar } from "@/components/layout/app-topbar";
import { useCurrentAccount } from "@/hooks/use-session";
import { accountQueries } from "@/queries/account";
import { tenantQueries } from "@/queries/tenants";
import { userQueries } from "@/queries/users";
import { useSessionStore } from "@/stores/session";

export const Route = createFileRoute("/_app")({
  beforeLoad: requireAuth,
  loader: async ({ context }) => {
    const [currentUser, tenants] = await Promise.all([
      context.queryClient.ensureQueryData(userQueries.me()),
      context.queryClient.ensureQueryData(tenantQueries.list()),
      context.queryClient.ensureQueryData(accountQueries.detail()),
    ]);

    context.queryClient.setQueryData(
      userQueries.detail(currentUser.id).queryKey,
      currentUser,
    );

    const store = useSessionStore.getState();
    store.setCurrentUserId(currentUser.id);

    const [firstTenant] = tenants;
    if (!firstTenant) {
      throw new Error("No tenants available for this account");
    }

    const tenantIds = new Set(tenants.map((t) => t.id));
    if (!store.currentTenantId || !tenantIds.has(store.currentTenantId)) {
      store.setCurrentTenantId(firstTenant.id);
    }
  },
  component: AppLayout,
});

function useDocumentTitle() {
  const account = useCurrentAccount();
  const matches = useMatches();

  const pageTitle = matches
    .map((match) => match.staticData.title)
    .filter((title, index, arr) => title && arr.indexOf(title) === index)
    .join(" - ");

  useEffect(() => {
    document.title = pageTitle
      ? `${pageTitle} | ${account.name}`
      : account.name;
  }, [pageTitle, account.name]);
}

function AppLayout() {
  useDocumentTitle();

  return (
    <ThemeProvider defaultTheme="system">
      <TooltipProvider>
        <SidebarProvider>
          <AppSidebar />
          <SidebarInset>
            <AppTopbar />
            <div className="flex min-w-0 flex-1 flex-col p-6">
              <Outlet />
            </div>
          </SidebarInset>
        </SidebarProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}
